import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORIES, HERO_IMAGE, formatDateRange } from "../lib/constants";
import { EventCard } from "../components/EventCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowRight, Sparkle, MapPin, Calendar, Fire } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Home() {
  const [events, setEvents] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [nlQuery, setNlQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/events?limit=8").then(({ data }) => setEvents(data.events)).catch(() => {});
    api.get("/events?featured_only=true&limit=3").then(({ data }) => setFeatured(data.events)).catch(() => {});
  }, []);

  const handleNLSearch = async (e) => {
    e.preventDefault();
    if (!nlQuery.trim()) return;
    setLoading(true);
    try {
      navigate(`/events?nl=${encodeURIComponent(nlQuery)}`);
    } catch {
      toast.error("Errore nella ricerca");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Hero" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
          <div className="grain-overlay" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 pb-16 lg:pb-24 w-full">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-primary" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">Raduni Passion · 2026</span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl font-black uppercase leading-[0.9] tracking-tighter max-w-5xl mb-8" data-testid="hero-title">
            Dove la <span className="text-primary">passione</span><br/>si incontra su strada
          </h1>
          <p className="max-w-2xl text-base sm:text-lg text-neutral-300 leading-relaxed mb-10">
            Scopri raduni, meetup e eventi per appassionati di auto e moto in tutta Europa. Ferrari, Harley, Porsche, Ducati — trova la tua tribù.
          </p>

          {/* NL Search */}
          <form onSubmit={handleNLSearch} className="max-w-3xl">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-500 mb-3 flex items-center gap-2">
              <Sparkle size={12} weight="fill" className="text-primary" /> Ricerca intelligente con AI
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                data-testid="hero-nl-search"
                value={nlQuery}
                onChange={(e) => setNlQuery(e.target.value)}
                placeholder='es: "raduni Ferrari a Parigi ad agosto"'
                className="flex-1 h-14 bg-black/50 border-white/20 focus:border-primary focus:ring-primary text-base rounded-sm"
              />
              <Button type="submit" disabled={loading} data-testid="hero-search-btn" className="h-14 px-8 bg-primary hover:bg-primary/90 text-white font-black uppercase tracking-wider rounded-sm">
                {loading ? "..." : "Cerca"} <ArrowRight size={16} weight="bold" className="ml-2" />
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* FEATURED EVENTS - sponsored slots */}
      {featured.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-24">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-white mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-primary rounded-full animate-pulse" /> Featured · Sponsored
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter">In evidenza</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((e, i) => <EventCard key={e.event_id} event={e} index={i} />)}
          </div>
        </section>
      )}

      {/* CATEGORIES BENTO */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-3">Le tribù</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter">Categorie</h2>
          </div>
          <Link to="/categories" className="hidden sm:flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-400 hover:text-primary transition-colors" data-testid="all-categories-link">
            Tutte le categorie <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {CATEGORIES.map((c, i) => {
            const isLarge = i === 0 || i === 3;
            return (
              <Link
                key={c.id}
                to={`/events?category=${c.id}`}
                data-testid={`category-tile-${c.id}`}
                className={`group relative overflow-hidden border border-border rounded-sm bg-card ${isLarge ? "md:col-span-6" : "md:col-span-3"} aspect-[4/3]`}
                style={{ transition: "border-color 0.3s cubic-bezier(0.4,0,0.2,1)" }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = "hsl(3 100% 59%)"}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = ""}
              >
                <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105" style={{ transition: "transform 0.6s cubic-bezier(0.4,0,0.2,1), opacity 0.3s" }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <div className="relative h-full flex flex-col justify-between p-6 lg:p-8">
                  <div className="text-xs font-black uppercase tracking-[0.3em] text-primary">{c.tag}</div>
                  <div>
                    <h3 className="font-display font-black text-2xl lg:text-4xl uppercase tracking-tighter leading-none">{c.name}</h3>
                    <div className="mt-3 flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity">
                      Esplora <ArrowRight size={12} weight="bold" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED EVENTS */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-3 flex items-center gap-2">
              <Fire size={12} weight="fill" /> In evidenza
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter">Prossimi raduni</h2>
          </div>
          <Link to="/events" className="hidden sm:flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-400 hover:text-primary transition-colors" data-testid="all-events-link">
            Tutti gli eventi <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {events.slice(0, 8).map((e, i) => <EventCard key={e.event_id} event={e} index={i} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24">
        <div className="border border-primary/30 rounded-sm p-12 lg:p-16 bg-gradient-to-br from-primary/10 to-transparent relative overflow-hidden">
          <div className="grid md:grid-cols-2 gap-8 items-center relative z-10">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4">Community driven</div>
              <h2 className="font-display text-3xl sm:text-5xl font-black uppercase tracking-tighter mb-6">Organizzi un raduno?<br/>Pubblicalo qui.</h2>
              <p className="text-neutral-300 max-w-lg leading-relaxed">
                Aggiungi il tuo evento in pochi minuti. Approvazione rapida, visibilità internazionale.
              </p>
            </div>
            <div className="flex md:justify-end">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-white font-black uppercase tracking-wider rounded-sm h-14 px-8" data-testid="cta-submit-btn">
                <Link to="/submit">Aggiungi evento <ArrowRight size={16} weight="bold" className="ml-2" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
